import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { api, fmtMXN } from "@/lib/api";
import { photoUrl, photoAlt } from "@/lib/photos";
import { ArrowUpRight } from "lucide-react";

export default function VillaCard({ property, index }) {
  const { t, lang } = useLang();
  const tagline = lang === "es" ? property.tagline_es : property.tagline_en;
  const ideal = lang === "es" ? property.ideal_for_es : property.ideal_for_en;
  const [coverUrl, setCoverUrl] = useState(property.card_image);
  const [coverAlt, setCoverAlt] = useState(property.name);

  useEffect(() => {
    // If a property explicitly pins its card_image (e.g., deliberate visual
    // variety vs the big hero), skip the auto-replace with DB portada[0].
    if (property.card_image_pin) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/photos?property_id=${property.id}`);
        if (cancelled) return;
        const portada = data?.sections?.portada;
        if (portada && portada.length > 0) {
          setCoverUrl(photoUrl(portada[0]));
          setCoverAlt(photoAlt(portada[0], property.name));
        }
      } catch {
        // keep fallback
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [property.id, property.card_image, property.card_image_pin, property.name]);

  return (
    <Link
      to={`/villas/${property.slug}`}
      data-testid={`villa-card-${property.id}`}
      className="group block bg-white border border-stone-200 hover:border-stone-400 transition-all hover:-translate-y-1"
    >
      <div className="relative aspect-[5/4] overflow-hidden bg-stone-200">
        <img
          src={coverUrl}
          alt={coverAlt}
          loading={index === 0 ? "eager" : "lazy"}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1500ms] ease-out"
        />
        <div className="absolute top-4 left-4 bg-[#E07A5F] text-white text-eyebrow px-3 py-1.5">
          0{index + 1}
        </div>
        <div className="absolute bottom-4 right-4 bg-stone-50/95 backdrop-blur text-stone-900 text-xs uppercase tracking-[0.15em] font-semibold px-3 py-1.5">
          {t("villas_home.from")} {fmtMXN(property.pricing.web_regular)} {t("villas_home.per_night")}
        </div>
      </div>

      <div className="p-6 md:p-8">
        <div className="flex items-start justify-between gap-4 mb-3">
          <h3 className="font-serif-display text-3xl md:text-4xl text-stone-900 leading-[1.05]">
            {property.name}
          </h3>
          <ArrowUpRight className="w-6 h-6 text-stone-400 group-hover:text-[#E07A5F] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all flex-shrink-0" />
        </div>
        <p className="font-serif-display italic text-stone-600 text-lg mb-5">{tagline}</p>

        <div className="grid grid-cols-4 border-t border-stone-200 pt-5 mb-5">
          {[
            { n: property.capacity, l: t("villas_home.capacity") },
            { n: property.bedrooms, l: t("villas_home.bedrooms") },
            { n: property.beds, l: t("villas_home.beds") },
            { n: property.baths, l: t("villas_home.baths") },
          ].map((s, i) => (
            <div key={i} className="text-left">
              <div className="font-serif-display text-2xl md:text-3xl text-stone-900 leading-none">{s.n}</div>
              <div className="text-[10px] uppercase tracking-wider text-stone-500 mt-1">{s.l}</div>
            </div>
          ))}
        </div>

        <p className="text-sm text-stone-600 mb-5">{ideal}</p>

        <div className="flex items-baseline justify-between border-t border-stone-200 pt-5">
          <div>
            <div className="text-xs text-stone-400 uppercase tracking-wider line-through">
              {t("villas_home.airbnb_was")} {fmtMXN(property.pricing.airbnb_regular)}
            </div>
            <div className="font-serif-display text-2xl text-[#E07A5F] mt-1">
              {fmtMXN(property.pricing.web_regular)}
              <span className="text-sm text-stone-500 ml-2">/ {t("villas_home.per_night")}</span>
            </div>
          </div>
          <span className="text-xs uppercase tracking-[0.18em] font-semibold text-stone-900 group-hover:text-[#E07A5F] transition-colors">
            {t("villas_home.view_property")} →
          </span>
        </div>
      </div>
    </Link>
  );
}
